import { centsToFormattedDollars } from '@fruitful/fruitils';
import { type MembershipBillingFrequency } from '@fruitful/types';
import {
    BodyMediumRegular,
    BodySmallRegular,
    H6Emphasized,
    LoadingSkeleton,
    XStack,
    YStack,
} from 'smoothie';

import {
    getBillingFrequencyLabel,
    getBillingFrequencyToggleCaption,
} from '../constants/premiumPricingCopy';
import {
    PREMIUM_BILLING_FREQUENCY_DISPLAY_ORDER,
    type UpgradeToPremiumPreview,
} from '../hooks/useUpgradeSubscription';

interface BillingFrequencyToggleProps {
    billingFrequency: MembershipBillingFrequency;
    onChange: (billingFrequency: MembershipBillingFrequency) => void;
    pricing: Partial<
        Record<MembershipBillingFrequency, UpgradeToPremiumPreview>
    >;
    disabled?: boolean;
}

const BillingFrequencyCard = ({
    frequency,
    amountDueNowCents,
    isSelected,
    onPress,
}: {
    frequency: MembershipBillingFrequency;
    amountDueNowCents: number | undefined;
    isSelected: boolean;
    onPress: () => void;
}) => (
    <YStack
        flex={1}
        gap={2}
        padding={16}
        borderRadius={16}
        borderWidth={1}
        borderColor={isSelected ? '$peachDark' : 'transparent'}
        backgroundColor="$gray100"
        alignItems="center"
        onPress={onPress}
        cursor="pointer"
        pressStyle={{ opacity: 0.8 }}
    >
        <BodyMediumRegular color="$peachDarkest">
            {getBillingFrequencyLabel(frequency)}
        </BodyMediumRegular>
        {amountDueNowCents == null ? (
            <LoadingSkeleton width={60} height={20} marginVertical={2} />
        ) : (
            <H6Emphasized>
                {centsToFormattedDollars(amountDueNowCents, false)}
            </H6Emphasized>
        )}
        <BodySmallRegular color="$gray1000">
            {getBillingFrequencyToggleCaption(frequency)}
        </BodySmallRegular>
    </YStack>
);

const BillingFrequencyToggle = ({
    billingFrequency,
    onChange,
    pricing,
    disabled = false,
}: BillingFrequencyToggleProps) => {
    return (
        <YStack
            gap={12}
            padding={16}
            borderRadius={16}
            backgroundColor="$gray200"
            width="100%"
        >
            <BodyMediumRegular>
                How would you like to be billed?
            </BodyMediumRegular>
            <XStack
                gap={12}
                width="100%"
                pointerEvents={disabled ? 'none' : 'auto'}
            >
                {PREMIUM_BILLING_FREQUENCY_DISPLAY_ORDER.map((frequency) => (
                    <BillingFrequencyCard
                        key={frequency}
                        frequency={frequency}
                        amountDueNowCents={
                            pricing[frequency]?.stripePreview.amountDueNowCents
                        }
                        isSelected={billingFrequency === frequency}
                        onPress={() => {
                            onChange(frequency);
                        }}
                    />
                ))}
            </XStack>
        </YStack>
    );
};

export default BillingFrequencyToggle;
